import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageSquare, Users, Search, Plus, Send, Paperclip, X, Check, CheckCheck, MoreVertical, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { InfoBanner } from '../../components/ui/info-banner';

export const BranchIletisim = () => {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // File attachment states
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Modal states
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  
  const messagesEndRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConversations();
    fetchAvailableUsers();
    
    // Poll for new messages every 3 seconds
    const interval = setInterval(() => {
      if (activeConversation) {
        fetchMessages(activeConversation.id, true);
      }
      fetchConversations();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [activeConversation]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/messaging/conversations`);
      setConversations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Conversations fetch error:', error);
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/messaging/users`);
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    try {
      const response = await axios.get(`${backendUrl}/api/messaging/conversations/${conversationId}/messages?limit=100`);
      setMessages(response.data);
      
      // Mark messages as read
      response.data.forEach(msg => {
        if (!msg.is_read) {
          axios.post(`${backendUrl}/api/messaging/messages/${msg.id}/read`).catch(console.error);
        }
      });
      
      // Scroll to bottom
      if (!silent) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (error) {
      console.error('Messages fetch error:', error);
    }
  };

  const handleConversationClick = (conversation) => {
    setActiveConversation(conversation);
    fetchMessages(conversation.id);
  };

  const handleCreateIndividualChat = async (userId) => {
    try {
      const response = await axios.post(`${backendUrl}/api/messaging/conversations`, {
        conversation_type: 'individual',
        participant_ids: [userId]
      });
      
      if (response.data.exists) {
        toast.info('Bu kişiyle sohbetiniz zaten var');
      } else {
        toast.success('Yeni sohbet oluşturuldu');
      }
      
      setShowNewChat(false);
      fetchConversations();
      
      // Open the conversation
      setTimeout(() => {
        const conv = conversations.find(c => c.id === response.data.conversation_id);
        if (conv) handleConversationClick(conv);
      }, 500);
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg).join(', ') : 'Sohbet oluşturulamadı'));
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Grup adı gereklidir');
      return;
    }
    
    if (selectedUsers.length < 2) {
      toast.error('En az 2 kişi seçmelisiniz');
      return;
    }
    
    try {
      const response = await axios.post(`${backendUrl}/api/messaging/conversations`, {
        conversation_type: 'group',
        participant_ids: selectedUsers,
        name: groupName
      });
      
      toast.success('Grup oluşturuldu');
      setShowNewGroup(false);
      setGroupName('');
      setSelectedUsers([]);
      fetchConversations();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg).join(', ') : 'Grup oluşturulamadı'));
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && !selectedFile) return;
    
    setSending(true);
    try {
      let fileData = null;
      
      // Upload file first if selected
      if (selectedFile) {
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        try {
          const uploadResponse = await axios.post(`${backendUrl}/api/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          fileData = {
            file_url: uploadResponse.data.url,
            file_name: selectedFile.name,
            file_type: selectedFile.type
          };
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          toast.error('Dosya yüklenemedi');
          setUploadingFile(false);
          setSending(false);
          return;
        }
        setUploadingFile(false);
      }
      
      await axios.post(`${backendUrl}/api/messaging/conversations/${activeConversation.id}/messages`, {
        content: messageText || (selectedFile ? `Dosya: ${selectedFile.name}` : ''),
        message_type: selectedFile ? 'file' : 'text',
        ...fileData
      });
      
      setMessageText('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchMessages(activeConversation.id);
      fetchConversations(); // Update last message
    } catch (error) {
      toast.error('Mesaj gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Dosya boyutu 10MB\'dan büyük olamaz');
        return;
      }
      setSelectedFile(file);
      toast.success(`Dosya seçildi: ${file.name}`);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getConversationName = (conversation) => {
    if (conversation.conversation_type === 'group') {
      return conversation.name || 'İsimsiz Grup';
    }
    // Individual - show other participant's name
    return conversation.participants[0]?.name || 'Kullanıcı';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return parts[0][0] + parts[1][0];
    }
    return name.substring(0, 2);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Dün';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('tr-TR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    }
  };

  const filteredConversations = conversations.filter(conv => 
    getConversationName(conv).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="branch-messaging-page">
      {/* InfoBanner */}
      <InfoBanner storageKey="branch-communication-info">
        İletişim panelini kullanarak diğer kullanıcılarla iletişiminizi sağlayabilir, belge gönderebilir ve iş grupları oluşturabilirsiniz.
      </InfoBanner>
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Mesajlar</h2>
          <p className="text-[hsl(var(--muted-foreground))]">Şube arkadaşlarınızla mesajlaşın</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowNewChat(true)}
            className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]" 
            data-testid="branch-new-message-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Yeni Mesaj
          </Button>
          <Button 
            onClick={() => setShowNewGroup(true)}
            variant="outline" 
            data-testid="branch-new-group-btn"
          >
            <Users className="h-4 w-4 mr-2" />
            Yeni Grup
          </Button>
        </div>
      </div>

      {/* Messaging Interface */}
      <div className="grid grid-cols-3 gap-4 h-[calc(100vh-12rem)]">
        {/* Conversation List */}
        <div className="col-span-1 bg-white rounded-lg border border-[hsl(var(--border))] flex flex-col">
          <div className="p-4 border-b border-[hsl(var(--border))]">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                placeholder="Sohbet ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
                data-testid="branch-search-conversations"
              />
            </div>
          </div>
          
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="p-4 text-center text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-[hsl(var(--muted-foreground))]">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">Henüz mesajınız yok</p>
                <p className="text-xs mt-2">Yeni bir sohbet başlatın</p>
              </div>
            ) : (
              <div className="divide-y divide-[hsl(var(--border))]">
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleConversationClick(conv)}
                    className={`p-4 cursor-pointer hover:bg-[hsl(var(--muted))] transition-colors ${
                      activeConversation?.id === conv.id ? 'bg-[hsl(var(--muted))]' : ''
                    }`}
                    data-testid="branch-conversation-item"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-[hsl(var(--brand-700))] text-white">
                          {conv.conversation_type === 'group' ? (
                            <Users className="h-4 w-4" />
                          ) : (
                            getInitials(getConversationName(conv))
                          )}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-sm truncate">
                            {getConversationName(conv)}
                          </h4>
                          {conv.last_message_at && (
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">
                              {formatTime(conv.last_message_at)}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-[hsl(var(--muted-foreground))] truncate">
                          {conv.last_message || 'Yeni sohbet'}
                        </p>
                        
                        {conv.unread_count > 0 && (
                          <Badge className="mt-1 bg-[hsl(var(--brand-700))] text-white">
                            {conv.unread_count} yeni
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Conversation View */}
        <div className="col-span-2 bg-white rounded-lg border border-[hsl(var(--border))] flex flex-col">
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback className="bg-[hsl(var(--brand-700))] text-white">
                      {activeConversation.conversation_type === 'group' ? (
                        <Users className="h-4 w-4" />
                      ) : (
                        getInitials(getConversationName(activeConversation))
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold">{getConversationName(activeConversation)}</h3>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      {activeConversation.conversation_type === 'group' 
                        ? `${activeConversation.participants.length + 1} katılımcı` 
                        : activeConversation.participants[0]?.position_name || 'Çalışan'}
                    </p>
                  </div>
                </div>
                
                <Button variant="ghost" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Henüz mesaj yok</p>
                      <p className="text-sm mt-2">İlk mesajı gönderin!</p>
                    </div>
                  ) : (
                    messages.map((msg, index) => {
                      const isOwn = msg.sender_id === activeConversation.participants[0]?.id ? false : true; // Simplified check
                      const showSender = activeConversation.conversation_type === 'group' && !isOwn;
                      
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                          data-testid="branch-message-item"
                        >
                          <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                            {showSender && (
                              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1 px-3">
                                {msg.sender_name}
                              </p>
                            )}
                            <div
                              className={`rounded-lg px-4 py-2 ${
                                isOwn
                                  ? 'bg-[hsl(var(--brand-700))] text-white'
                                  : 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]'
                              }`}
                            >
                              {msg.file_url && (
                                <div className="mb-2">
                                  {/* Image preview */}
                                  {(msg.file_type?.startsWith('image/') || 
                                    msg.file_name?.match(/\.(png|jpg|jpeg|gif|webp)$/i)) && (
                                    <div className="mb-2">
                                      <img 
                                        src={msg.file_url} 
                                        alt={msg.file_name || 'Görsel'}
                                        className="max-w-full max-h-48 rounded-lg cursor-pointer hover:opacity-90"
                                        onClick={() => window.open(msg.file_url, '_blank')}
                                      />
                                    </div>
                                  )}
                                  
                                  {/* File info and download */}
                                  <div className={`p-2 rounded flex items-center gap-2 ${isOwn ? 'bg-white/20' : 'bg-black/10'}`}>
                                    <Paperclip className="h-4 w-4 flex-shrink-0" />
                                    <span className="text-sm flex-1 truncate">{msg.file_name || 'Dosya'}</span>
                                    <a 
                                      href={msg.file_url} 
                                      download={msg.file_name || 'dosya'}
                                      className={`p-1 rounded hover:bg-white/20 ${isOwn ? 'text-white' : 'text-[hsl(var(--brand-700))]'}`}
                                      title="İndir"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        const link = document.createElement('a');
                                        link.href = msg.file_url;
                                        link.download = msg.file_name || 'dosya';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                    >
                                      <Download className="h-4 w-4" />
                                    </a>
                                  </div>
                                </div>
                              )}
                              <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                              <div className="flex items-center gap-1 mt-1 justify-end">
                                <span className={`text-xs ${isOwn ? 'text-white/70' : 'text-[hsl(var(--muted-foreground))]'}`}>
                                  {new Date(msg.sent_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {isOwn && (
                                  msg.read_by.length > 1 ? (
                                    <CheckCheck className="h-3 w-3 text-white/70" />
                                  ) : (
                                    <Check className="h-3 w-3 text-white/70" />
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Composer */}
              <div className="p-4 border-t border-[hsl(var(--border))]">
                {/* Selected file preview */}
                {selectedFile && (
                  <div className="mb-2 flex items-center gap-2 p-2 bg-[hsl(var(--muted))] rounded-md">
                    <Paperclip className="h-4 w-4 text-[hsl(var(--brand-700))]" />
                    <span className="text-sm flex-1 truncate">{selectedFile.name}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={removeSelectedFile}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                <div className="flex items-end gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.zip,.rar"
                  />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mb-2"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    data-testid="branch-attach-file-btn"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  
                  <div className="flex-1">
                    <Input
                      placeholder="Mesajınızı yazın..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="resize-none"
                      data-testid="branch-message-input"
                    />
                  </div>
                  
                  <Button
                    onClick={handleSendMessage}
                    disabled={(!messageText.trim() && !selectedFile) || sending || uploadingFile}
                    className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
                    data-testid="branch-send-message-btn"
                  >
                    {uploadingFile ? (
                      <span className="animate-spin">...</span>
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[hsl(var(--muted-foreground))]">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p>Bir sohbet seçin veya yeni bir sohbet başlatın</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Individual Chat Modal */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent data-testid="branch-new-chat-modal">
          <DialogHeader>
            <DialogTitle>Yeni Mesaj</DialogTitle>
            <DialogDescription>Şube arkadaşınızla sohbet başlatın</DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <ScrollArea className="h-[300px]">
              <div className="space-y-2">
                {availableUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleCreateIndividualChat(user.id)}
                    className="flex items-center gap-3 p-3 rounded-md hover:bg-[hsl(var(--muted))] cursor-pointer transition-colors"
                    data-testid="branch-user-item"
                  >
                    <Avatar>
                      <AvatarFallback className="bg-[hsl(var(--brand-700))] text-white">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        {user.position_name || user.profession_name || 'Çalışan'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Group Modal */}
      <Dialog open={showNewGroup} onOpenChange={setShowNewGroup}>
        <DialogContent data-testid="branch-new-group-modal">
          <DialogHeader>
            <DialogTitle>Yeni Grup Oluştur</DialogTitle>
            <DialogDescription>Şube arkadaşlarınızla grup sohbeti oluşturun</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="branch-group-name">Grup Adı *</Label>
              <Input
                id="branch-group-name"
                placeholder="Grup adını girin..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                data-testid="branch-group-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Katılımcılar (En az 2 kişi seçin)</Label>
              <ScrollArea className="h-[200px] border rounded-md p-2">
                <div className="space-y-2">
                  {availableUsers.map((user) => (
                    <div key={user.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`branch-user-${user.id}`}
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                          }
                        }}
                      />
                      <label
                        htmlFor={`branch-user-${user.id}`}
                        className="text-sm flex-1 cursor-pointer flex items-center gap-2"
                      >
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="bg-[hsl(var(--brand-700))] text-white text-xs">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span>{user.name}</span>
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                          ({user.position_name || 'Çalışan'})
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {selectedUsers.length} kişi seçildi
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowNewGroup(false);
              setGroupName('');
              setSelectedUsers([]);
            }}>
              İptal
            </Button>
            <Button
              onClick={handleCreateGroup}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              disabled={!groupName.trim() || selectedUsers.length < 2}
              data-testid="branch-create-group-btn"
            >
              Grup Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchIletisim;
